import React, { useState, ChangeEvent, useEffect } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { Grid, TextField, FormHelperTextProps } from '@material-ui/core';
import { useStyles } from '../../../pages/styles';
import { useStyles as myStyles } from './MainPageEditor.styles';
import { LeavePrompt } from './../../Prompt/LeavePrompt/LeavePrompt.component';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import { ButtonOne } from '../../ButtonOne/ButtonOne.component';
import ReactQuill from 'react-quill';
import { modules, sanitizeHtmlContent } from '../../../utils/reactQuillUtils';

const HTMLCONTENT_MAX_LENGTH = 5010;

interface MainPageEditorProps {
  title: string;
  onCancel: () => void;
  onSave: (htmlContent: string) => void;
  onChange: () => void;
  htmlContent: string;
}

export const MainPageEditor: React.FC<MainPageEditorProps> = ({
  title,  
  onCancel,
  onSave,
  htmlContent: htmlContentProp,
}) => {
  const classes = myStyles();
  const globalClasses = useStyles();
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [htmlContent, setHtmlContent] = useState<string>(htmlContentProp ?? '');
  const [rawHtmlContent, setRawHtmlContent] = useState<string>(htmlContentProp ?? '');
  const [hasHtmlContentFocus, setHasHtmlContentFocus] = useState<boolean>(false);
  const [hasContactInfoFocus, setHasContactInfoFocus] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [isHtmlEditor, setIsHtmlEditor] = useState<boolean>(true);

  const handleHtmlContentChange = (value: string) => {
    setHtmlContent(value);
  };


  useEffect(() => {
    setIsDataChanged(htmlContentProp !== htmlContent);
  }, [htmlContentProp, htmlContent]);

  const isHtmlContentTooLong = htmlContent.length > HTMLCONTENT_MAX_LENGTH;

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };
  
  const handleSaveRawHtmlContent = (htmlContent: string) => {
    setRawHtmlContent(htmlContent);
    setHtmlContent(htmlContent);
  }

  const switchHtmlEditor = () => {
    isHtmlEditor ? setRawHtmlContent(htmlContent) : setHtmlContent(rawHtmlContent);
    setIsHtmlEditor(!isHtmlEditor);
  }

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="md"
        title={title}
        autoHeight
        onCancel={handleCancel}
        onConfirm={() => onSave(htmlContent)}
        confirmButtonDisabled={
          isHtmlContentTooLong ||
          !isDataChanged
        }
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
        <Grid item xs={12} className={classes.item}>
                <div style={{ textAlign: 'left' }}>
                  <ButtonOne
                    className={globalClasses.primary}
                    style={{ marginBottom: 15 }}
                    variant="contained"
                    aria-label="Edytuj"
                    onClick={switchHtmlEditor}
                  >{isHtmlEditor ? 'Kod HTML' : 'Edytor HTML'}</ButtonOne>
                </div>
                
                {isHtmlEditor && <>
                  <ReactQuill 
                    placeholder= "Opis nagłówka" 
                    theme="snow" 
                    className={isHtmlContentTooLong ? `${globalClasses.reactQuill} ${globalClasses.reactQuillWrong}` : globalClasses.reactQuill} 
                    value={htmlContent} 
                    onChange={setHtmlContent} 
                    modules={modules}
                    onFocus={() => setHasHtmlContentFocus(true)}
                    onBlur={() => setHasHtmlContentFocus(false)} />
                  {isHtmlContentTooLong ? 
                  <div className={classes.answerTooLong}>
                    <div>Podany opis jest za długi</div>
                  </div>
                  : undefined}
                </>}

                {!isHtmlEditor && <TextField
                  value={rawHtmlContent}
                  multiline
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleSaveRawHtmlContent(e.target.value)}
                  onFocus={() => setHasHtmlContentFocus(true)}
                  onBlur={() => setHasHtmlContentFocus(false)}
                  label="Opis nagłówka"
                  variant="outlined"
                  fullWidth
                  minRows="7"
                  maxRows="15"
                  error={isHtmlContentTooLong}
                  FormHelperTextProps={
                    {
                      component: 'div',
                    } as Partial<FormHelperTextProps<'p'>>
                  }
                  helperText={
                    <ValidationRow
                      helperText={isHtmlContentTooLong ? 'Podany opis jest za długi' : ''}
                      limitsVisible={false}
                      valueLength={htmlContent.length}
                      limit={HTMLCONTENT_MAX_LENGTH}
                      withError={isHtmlContentTooLong}
                    />
                  }
                />}
              </Grid>
        </Grid>
        {!isHtmlEditor && <div className={classes.item}>
              <h3>Podgląd HTML:</h3>
              {<div className={`ql-editor ${classes.showHtml}`} dangerouslySetInnerHTML={{ __html: sanitizeHtmlContent(rawHtmlContent) }} />}
            </div>}
      <br />
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
